import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Tooltip,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

// ─── Reusable: editable agent list ───────────────────────────────────────────
const AgentList = ({ agents, onChange, readOnly = false }) => {
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    onChange([...agents, { agentName: name, agentEmail: newEmail.trim() }]);
    setNewName('');
    setNewEmail('');
  };

  const handleDelete = (idx) => {
    onChange(agents.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      <List dense disablePadding>
        {agents.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
            אין סוכנים מוגדרים
          </Typography>
        )}
        {agents.map((agent, idx) => (
          <ListItem
            key={idx}
            disableGutters
            sx={{ py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}
          >
            <PersonIcon sx={{ fontSize: 18, color: '#0054A5', mr: 1, flexShrink: 0 }} />
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={500}>
                  {idx + 1}. {agent.agentName}
                </Typography>
              }
              secondary={agent.agentEmail || undefined}
              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
            />
            {!readOnly && (
              <ListItemSecondaryAction>
                <Tooltip title="הסר סוכן">
                  <IconButton size="small" color="error" onClick={() => handleDelete(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>

      {!readOnly && (
        <Box display="flex" gap={1} mt={1.5} alignItems="flex-start">
          <TextField
            size="small"
            label="שם סוכן (AGENTNAME)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="אימייל (אופציונלי)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            sx={{ flex: 2 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!newName.trim()}
            sx={{ flexShrink: 0, height: 40 }}
          >
            הוסף
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Manager view: edit own agents ───────────────────────────────────────────
const ManagerAgentView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [managerName, setManagerName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await execute('GET', `${BASE_URL}/api/tradeDelay/agents`);
      setAgents(res?.agents || []);
      setManagerName(res?.managerName || '');
    } catch {
      setError('שגיאה בטעינת הסוכנים');
    } finally {
      setLoading(false);
    }
  }, [execute]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await execute('PUT', `${BASE_URL}/api/tradeDelay/agents`, { agents });
      setAgents(res?.agents || agents);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('שגיאה בשמירת הסוכנים');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" color="#0054A5">
            הסוכנים שלי
          </Typography>
          {managerName && (
            <Typography variant="body2" color="text.secondary">
              {managerName}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${agents.length} סוכנים`}
          size="small"
          sx={{ bgcolor: '#e8f0fe', color: '#0054A5' }}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        הזן את שמות הסוכנים בדיוק כפי שהם מופיעים בשדה <strong>AGENTNAME</strong> ב-Priority.
        הזמנות יסוננו לפי רשימה זו עבורך ועבור העובדים תחתיך.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>הסוכנים נשמרו בהצלחה</Alert>}

      <AgentList agents={agents} onChange={setAgents} />

      <Box mt={2}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{ bgcolor: '#0054A5', '&:hover': { bgcolor: '#003d7a' } }}
        >
          שמור שינויים
        </Button>
      </Box>
    </Box>
  );
};

// ─── Employee view: read-only ─────────────────────────────────────────────────
const EmployeeAgentView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [managerName, setManagerName] = useState('');

  useEffect(() => {
    execute('GET', `${BASE_URL}/api/tradeDelay/agents`)
      .then((res) => {
        setAgents(res?.agents || []);
        setManagerName(res?.managerName || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [execute]);

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  return (
    <Box>
      <Typography variant="h6" color="#0054A5" mb={0.5}>
        סוכני הצוות
      </Typography>
      {managerName && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          מנהל: {managerName}
        </Typography>
      )}
      {agents.length === 0 && !managerName && (
        <Alert severity="info">לא שויכת למנהל עם רשימת סוכנים מוגדרת</Alert>
      )}
      <AgentList agents={agents} onChange={() => {}} readOnly />
    </Box>
  );
};

// ─── Admin view: all managers ────────────────────────────────────────────────
const AdminManagerCard = ({ manager, execute, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [agents, setAgents] = useState(manager.agents || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const res = await execute(
        'PUT',
        `${BASE_URL}/api/tradeDelay/managers/${manager._id}/agents`,
        { agents }
      );
      setAgents(res?.agents || agents);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved && onSaved(manager._id, res?.agents || agents);
    } catch {
      setError('שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f8ff' } }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography fontWeight={600} color="#1f2937">
            {manager.Name} {manager.LastName}
          </Typography>
          <Chip
            label={`${agents.length} סוכנים`}
            size="small"
            sx={{ bgcolor: agents.length ? '#e8f0fe' : '#f3f4f6', color: agents.length ? '#0054A5' : '#6b7280' }}
          />
          {manager.Email && (
            <Typography variant="caption" color="text.secondary">
              {manager.Email}
            </Typography>
          )}
        </Box>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box px={2} py={2}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 1 }}>נשמר בהצלחה</Alert>}
          <AgentList agents={agents} onChange={setAgents} />
          <Box mt={1.5}>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: '#0054A5', '&:hover': { bgcolor: '#003d7a' } }}
            >
              שמור
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

const AdminAgentView = ({ execute }) => {
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    execute('GET', `${BASE_URL}/api/tradeDelay/allManagersAgents`)
      .then((res) => setManagers(res?.managers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [execute]);

  const handleSaved = (managerId, newAgents) => {
    setManagers((prev) =>
      prev.map((m) => m._id === managerId ? { ...m, agents: newAgents } : m)
    );
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  return (
    <Box>
      <Typography variant="h6" color="#0054A5" mb={0.5}>
        ניהול סוכנים — כל המנהלים
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        הגדר רשימת סוכנים לכל מנהל. ההזמנות יסוננו לפי רשימה זו.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        שמות הסוכנים חייבים להתאים בדיוק לשדה <strong>AGENTNAME</strong> ב-Priority.
      </Alert>

      {managers.length === 0 && (
        <Alert severity="warning">לא הוגדרו מנהלים במערכת ההשהיות</Alert>
      )}

      {managers.map((manager) => (
        <AdminManagerCard
          key={manager._id}
          manager={manager}
          execute={execute}
          onSaved={handleSaved}
        />
      ))}
    </Box>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────
const AgentManagementTab = ({ currentUser, execute }) => {
  const isAdmin = currentUser?.roles?.some((r) => r.admin);
  const isManager = currentUser?.tradeDelayRole === 'manager';

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <PersonIcon sx={{ color: '#0054A5' }} />
        <Typography variant="h6" color="#0054A5">
          ניהול סוכנים
        </Typography>
      </Box>

      {isAdmin ? (
        <AdminAgentView execute={execute} />
      ) : isManager ? (
        <ManagerAgentView execute={execute} />
      ) : (
        <EmployeeAgentView execute={execute} />
      )}
    </Box>
  );
};

export default AgentManagementTab;
